import { Flipper, FlipperProps, Text as FormattedText, Label } from '@components-deprecated'

import { amountToDisplayValue, Color, Flex, Spacing, Text } from '@billy/design-system'
import i18next from 'i18next'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import { SpecificState } from '../../../../types/reduxSaga-deprecated'
import { Account } from '../../../app/accounts/types'
import { bankBalanceRequested, setBalanceBarOpen } from '../../action-creators'
import { PrimoUltimoBalance } from '../../types'
import * as Styled from './styles'

export type BalanceBarProps = FlipperProps

const BankAccount = ({
  title,
  no,
  noBankAccount,
}: {
  title: string
  no: string | React.ReactNode
  noBankAccount: boolean
}) => (
  <Flex gap={Spacing.S16}>
    <Text weight="medium" color={Color.Lavender500}>
      {noBankAccount ? <Label variant="danger">{title}</Label> : title}
    </Text>
    <Text color={Color.Lavender500}>{no}</Text>
  </Flex>
)

const Outstanding = ({ title, amount = 0 }: { title: string; amount: number }) => (
  <Flex gap={Spacing.S6}>
    <Text colorVariant="secondary">{title}:</Text>
    <Text colorVariant="primary" weight="medium">
      {amountToDisplayValue(amount)}
    </Text>
  </Flex>
)

const InfoLine = ({
  title,
  info,
  type = 'text',
}: {
  title: string | React.ReactNode
  info: string
  type?: 'text' | 'date' | 'currency'
}) => (
  <Styled.InfoLineContainer>
    <Text weight="medium" color={Color.Lavender500}>
      {title}
    </Text>
    <FormattedText type={type}>{info}</FormattedText>
  </Styled.InfoLineContainer>
)

const BankNameInfoLine = ({ account, isBalanceBarOpen }: { account: Account; isBalanceBarOpen: boolean }) => {
  const accountResolved = useSelector((state: SpecificState) => state.app.accountResolved)
  const noBankAccount = !account.name && !account.bankName
  const bankName = account.name || account.bankName || i18next.t('bankreconciliation.balancebar.no_account_selected')
  const bankKonto = `${account.accountNo || ''} ${account.bankAccountNo || ''}`

  if (!accountResolved) {
    return <div></div>
  }

  if (isBalanceBarOpen) {
    const name = (
      <Flex gap={Spacing.S8} alignItems="center">
        {noBankAccount ? (
          <Label variant="danger">{bankName}</Label>
        ) : (
          <Text weight="medium" color={Color.Lavender500}>
            {bankName}
          </Text>
        )}
      </Flex>
    )

    return <InfoLine title={name} info={bankKonto} />
  }

  const konto = (
    <Flex gap={Spacing.S8} alignItems="center">
      {bankKonto}
    </Flex>
  )

  return <BankAccount title={bankName} no={konto} noBankAccount={noBankAccount} />
}

export const BalanceBar = ({ ...rest }: BalanceBarProps) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [isOpen, setIsOpen] = useState(false)

  const account = useSelector((state: SpecificState) => state.app.account || ({} as Account))
  const bankConnection = useSelector((state: SpecificState) => state.bankReconciliation.bankConnection)

  const latestBankLineEntryDate = useSelector(
    (state: SpecificState) => state.bankReconciliation.latestBankLineEntryDate,
  )
  const lastPullTime = bankConnection?.lastSynchronizedAt || latestBankLineEntryDate || undefined

  const primoUltimoBalance = useSelector((state: SpecificState) => state.bankReconciliation.primoUltimoBalance)
  const filters = useSelector((state: SpecificState) => state.bankReconciliation.filters)

  const { bankEndBalance, bankStartBalance, billyEndBalance, billyStartBalance } =
    (primoUltimoBalance as Required<PrimoUltimoBalance>) || {}

  const bankBalance = bankEndBalance
  const billyBalance = billyEndBalance

  const difference = billyBalance - bankBalance
  const startDifference = billyStartBalance - bankStartBalance
  const endDifference = billyEndBalance - bankEndBalance

  const toggleFlipSide = () => {
    setIsOpen((prevState: boolean) => !prevState)
    dispatch(setBalanceBarOpen({ isOpen: !isOpen }))
  }

  useEffect(() => {
    if (account?.id && filters) {
      dispatch(bankBalanceRequested())
    }
  }, [account, dispatch, filters])

  return (
    <Flipper onFlipSide={toggleFlipSide} {...rest}>
      <Flex justifyContent="space-between" width="100%">
        <BankNameInfoLine account={account} isBalanceBarOpen={isOpen} />

        <Flex justifyContent="flex-end" alignItems="center" gap={Spacing.S32}>
          <Text color={Color.Lavender500}>{t('bankreconciliation.balancebar.endbalance')}</Text>
          <Outstanding title="Bank" amount={bankBalance} />
          <Outstanding title="Billy" amount={billyBalance} />
          <Outstanding title={t('bankreconciliation.balancebar.difference')} amount={difference} />
        </Flex>
      </Flex>

      <Flex justifyContent="space-between" width="100%">
        <Styled.AccountDataContainer>
          <BankNameInfoLine account={account} isBalanceBarOpen={isOpen} />
          {!!lastPullTime && (
            <InfoLine title={t('bankreconciliation.balancebar.latestimportfrombank')} info={lastPullTime} type="date" />
          )}
        </Styled.AccountDataContainer>

        <Styled.BalanceTable>
          <Styled.GridLine>
            <Text> </Text>
            <Text weight="medium" color={Color.Lavender500}>
              Bank
            </Text>
            <Text weight="medium" color={Color.Lavender500}>
              Billy
            </Text>
            <Text weight="medium" color={Color.Lavender500}>
              {t('bankreconciliation.balancebar.difference')}
            </Text>
          </Styled.GridLine>
          <Styled.GridLine>
            <Text color={Color.Lavender500} weight="medium">
              {t('bankreconciliation.balancebar.startbalance')}
            </Text>
            <FormattedText type="currency">{bankStartBalance}</FormattedText>
            <FormattedText type="currency">{billyStartBalance}</FormattedText>
            <FormattedText type="currency">{startDifference}</FormattedText>
          </Styled.GridLine>
          <Styled.GridLine>
            <Text color={Color.Lavender500} weight="medium">
              {t('bankreconciliation.balancebar.endbalance')}
            </Text>
            <FormattedText type="currency">{bankEndBalance}</FormattedText>
            <FormattedText type="currency">{billyEndBalance}</FormattedText>
            <FormattedText type="currency">{endDifference}</FormattedText>
          </Styled.GridLine>
        </Styled.BalanceTable>
      </Flex>
    </Flipper>
  )
}
